<script setup>

    import LatteRatingBar from '@/Components/Feedback/LatteRatingBar.vue'
    import LatteStaffLabel from '@/Components/LatteStaffLabel.vue'
    import { computed, ref, watch } from 'vue'
    import moment from 'moment'
    import { handleBlur } from '@/Helpers'
    import { IconSnooze20Filled } from '@iconify-prerendered/vue-fluent'

    const caregiverCoffeeBaseUrl = import.meta.env.VITE_COFFEE_BASEURL + '/user/recruiter-candidates/personalinfo/id/'
    const clientCoffeeBaseUrl = import.meta.env.VITE_COFFEE_BASEURL + '/staff/sales-manager/information/pid/'
    const defaultProfileImage = '/default-profile.png'

    const props = defineProps({
        caregiver: {
            type: Object,
            required: true
        },
        link: {
            type: Boolean,
            default: false
        },
        badge: {
            type: Object,
            default: null
        },
        job: {
            type: Object,
            default: null
        },
        ringColor: {
            type: String,
            default: null
        }
    })

    const ringClass = computed(() => {
        const color = props.ringColor

        if (color) {
            const colorMap = {
                'green': 'ring-lifeworx-green-500',
                'yellow': 'ring-lifeworx-yellow-500',
                'red': 'ring-lifeworx-red-500',
            }

            return colorMap[color] || 'ring-stone-300';
        }

        return props.job ? 'ring-stone-300' : 'ring-stone-200/50';
    })

    const textClass = computed(() => {
        const color = props.ringColor

        if (color) {
            const colorMap = {
                'green': 'text-lifeworx-green-600',
                'yellow': 'text-lifeworx-yellow-600',
                'red': 'text-lifeworx-red-600',
            }

            return colorMap[color] || 'text-stone-300';
        }

        return props.job ? 'text-stone-600' : '';
    })

    const resolvedImageUrl = ref(defaultProfileImage)
    const isLoadingImage = ref(true)
    let currentPreload = null

    function preloadProfileImage(url) {
        if (!url) {
            if (resolvedImageUrl.value === defaultProfileImage) {
                isLoadingImage.value = false
                return;
            }
            resolvedImageUrl.value = defaultProfileImage
            isLoadingImage.value = false
            currentPreload = null
            return;
        }

        if (resolvedImageUrl.value === url) {
            return;
        }

        isLoadingImage.value = true
        const img = new Image()
        currentPreload = img
        img.onload = () => {
            if (currentPreload === img) {
                resolvedImageUrl.value = url
                isLoadingImage.value = false
            }
        }
        img.onerror = () => {
            if (currentPreload === img) {
                resolvedImageUrl.value = defaultProfileImage
                isLoadingImage.value = false
            }
        }
        img.src = url
    }

    watch(() => props.caregiver.profile_image_url, (url) => {
        preloadProfileImage(url)
    }, { immediate: true })

</script>

<template>

    <div class="w-full py-2 pl-2 flex items-center gap-4">
        <div class="flex-shrink-0">
            <OverlayBadge
                :unstyled="!badge"
                :value="badge?.value > 0 ? badge.value : null"
                severity="info"
                :pt="{ pcBadge: { root: badge?.value > 0 ? '!bg-lifeworx-purple-500/30 backdrop-blur border text-lifeworx-purple-500 !outline-lifeworx-purple-400 m-1.5 text-[0.8rem] z-40' : 'hidden' } }"
            >
                <div
                    class="relative rounded-full overflow-clip size-16 ring-2 ring-offset-2 z-30 bg-lifeworx-blue-800/5"
                    :class="ringClass"
                >
                    <a
                        v-if="link"
                        :href="caregiverCoffeeBaseUrl + props.caregiver.coffee_job_profile_id"
                        target="_blank"
                    >
                        <img
                            :src="resolvedImageUrl"
                            :alt="props.caregiver.first_name + ' ' + props.caregiver.last_name"
                            class="object-cover size-16"
                        />
                    </a>
                    <img
                        v-else
                        :src="resolvedImageUrl"
                        :alt="props.caregiver.first_name + ' ' + props.caregiver.last_name"
                        class="object-cover size-16"
                    />
                    <Skeleton
                        v-if="isLoadingImage"
                        size="5rem"
                        class="absolute size-20 -top-20 -z-10 bg-lifeworx-blue-800/5"
                    />
                </div>
            </OverlayBadge>
        </div>

        <div
            class="flex flex-col gap-0.5"
            ref="target"
        >
            <h4 class="tracking-wide text-lifeworx-blue-800 font-semibold text-lg leading-normal flex items-center">
                <a
                    v-if="link"
                    :href="caregiverCoffeeBaseUrl + props.caregiver.coffee_job_profile_id"
                    target="_blank"
                    class="hover:underline"
                >
                    {{ props.caregiver.first_name + ' ' + props.caregiver.last_name }}
                </a>
                <span v-else>
                    {{ props.caregiver.first_name + ' ' + props.caregiver.last_name }}
                </span>
                <span
                    v-if="props.caregiver.status === 'inactive'"
                    class="flex justify-center items-center ml-1 rounded-full bg-white/50 overflow-hidden"
                    v-tooltip="{ value: 'Inactive', pt: { text: 'text-sm' } }"
                >
                    <span class="bg-lifeworx-blue-500/5 size-full p-0.5"><IconSnooze20Filled class="size-5 text-lifeworx-blue-600 " /></span>
                </span>
            </h4>

            <div class="flex items-center gap-3 pb-1">

                <LatteStaffLabel
                    :staff="props.caregiver.assigned_recruiter"
                    size="xs"
                    :actionable="false"
                />

                <template v-if="props.caregiver.last_worked_for_client">

                    <div
                        :class="[
                             'text-xl font-semibold cursor-default items-center flex leading-tight text-lifeworx-blue-800/20'
                        ]"
                    >&bull;</div>

                    <VDropdown
                        v-if="props.caregiver.client_job?.latest_rating"
                        :triggers="['hover', 'click']"
                        :popperTriggers="['hover']"
                        :distance="10"
                        :skidding="-10"
                        :theme="'lifeworx-' + props.caregiver.client_job.latest_rating.status + '-outline'"
                        placement="top-start"
                        :autoHide="true"
                        @apply-hide="handleBlur"
                    >
                        <div
                            :class="[
                                textClass,
                                 'rounded-full py-1 px-2 bg-lifeworx-blue-300/10 hover:bg-lifeworx-blue-300/5 text-xs font-bold cursor-pointer items-center flex leading-tight'
                            ]"
                        >
                            Last worked here {{ moment(props.caregiver.last_worked_for_client).utc().from(moment.utc()) }}
                        </div>

                        <template #popper>
                            <div class="p-2 w-full">
                                <div class="flex text-sm space-x-1.5 pb-2">
                                    <LatteStaffLabel
                                        :staff="props.caregiver.client_job.latest_rating.created_by_staff"
                                    />
                                    <span
                                        class="text-zinc-400 font-semibold cursor-default"
                                        :title="moment(props.caregiver.client_job.latest_rating.created_at).utc().format('MMMM Do YYYY, h:mm:ss a')"
                                    >
                                        {{ moment(props.caregiver.client_job.latest_rating.created_at).utc().from(moment.utc()) }}
                                    </span>
                                </div>
                                <div class="flex text-sm space-x-1.5 pb-2">
                                    <h4 class="tracking-wide text-lifeworx-blue-700 font-semibold text-sm">
                                        <span class="text-zinc-400">Re: Client </span>
                                        <a
                                            :href="clientCoffeeBaseUrl + props.caregiver.client_job.client.coffee_user_profile_id"
                                            target="_blank"
                                            class="hover:underline"
                                        >
                                            {{ props.caregiver.client_job.client.first_name + ' ' + props.caregiver.client_job.client.last_name }}
                                        </a>
                                    </h4>
                                </div>
                                <div class="max-w-md px-4 pb-4">
                                    <span
                                        class="text-sm"
                                        :class="{ 'italic font-semibold text-zinc-400': !props.caregiver.client_job.latest_rating.note }"
                                    >
                                        {{ props.caregiver.client_job.latest_rating.note ? props.caregiver.client_job.latest_rating.note.content : 'No note added' }}
                                    </span>
                                </div>
                            </div>
                        </template>
                    </VDropdown>

                    <div
                        v-else
                        :class="[
                            textClass,
                             'text-xs font-semibold cursor-default items-center flex leading-tight text-stone-400'
                        ]"
                    >
                        Last worked here {{  props.caregiver.last_worked_for_client ? moment(props.caregiver.last_worked_for_client).utc().from(moment.utc()) : 'N/A' }}
                    </div>

                </template>

            </div>

            <div class="w-48">
                <LatteRatingBar
                    :subject="props.caregiver"
                    subjectType="caregiver"
                    :job="job"
                />
            </div>
        </div>
    </div>

</template>
